import type { Statement, StatementSegment } from '../stores/types'
import { isStatementProcessing } from './statement'

// Where the brokerage read of a project stands, for the Portfolio tab and the Overview card.
export type SecuritiesReadState = 'ready' | 'reading' | 'failed' | 'none'

// Kinds that are brokerage on their own. A mixed statement is often just bank plus card, so it only
// counts as reading once lukita puts the "processing" marker on it.
const BROKERAGE_ONLY_KINDS = ['investment', 'retirement']

const hasHoldings = (segment: StatementSegment) =>
  (segment.securitiesDetail?.accounts?.length ?? 0) > 0

const isMarkedReading = (segment: StatementSegment) =>
  segment.securitiesDetail?.status === 'processing'

// A beta brokerage read the team is checking. The statement reads "succeeded" with no rows until then.
export const isSegmentHeldForTeam = (segment: StatementSegment) =>
  isMarkedReading(segment) && segment.securitiesDetail?.heldForTeam === true

export const isStatementHeldForTeam = (statement: Statement) =>
  (statement.segments ?? []).some(isSegmentHeldForTeam)

// lukita stores "failed" when the read dies. A needs_review with no accounts means the engine
// found a securities side but could not read any account from it.
const isReadFailed = (segment: StatementSegment) => {
  const status = segment.securitiesDetail?.status
  return (
    status === 'failed' || (status === 'needs_review' && !hasHoldings(segment))
  )
}

const isStatementReading = (statement: Statement) =>
  (BROKERAGE_ONLY_KINDS.includes(statement.kind) &&
    isStatementProcessing(statement)) ||
  (statement.segments ?? []).some(isMarkedReading)

// Holdings always show. The reading and failed states are beta promises, so only a project with
// brokerage extraction on gets them.
export const securitiesReadState = (
  statements: Statement[],
  isBrokerageEnabled: boolean,
): SecuritiesReadState => {
  const segments = statements.flatMap((statement) => statement.segments ?? [])
  if (segments.some(hasHoldings)) return 'ready'
  if (!isBrokerageEnabled) return 'none'
  if (statements.some(isStatementReading)) return 'reading'
  if (segments.some(isReadFailed)) return 'failed'
  return 'none'
}
